/**
 * Bridges the editor canvas snapping engine with the recursive canvas nodes.
 *
 * `EditorCanvas` owns the snap index, the guide overlay state and the grouped
 * drag gesture; nodes only consume this contract so no prop has to be threaded
 * through the whole node recursion.
 */

import { createContext, useContext } from "react";
import type React from "react";
import type {
  SnapPoint,
  SnapRect,
} from "../../../../util/src/layout/drag-snap";

export interface CanvasDragSnapRequest {
  /** Ids currently being moved (a single node or the whole selection). */
  movingIds: string[];
  /** Bounding box at the raw drag position, in canvas space. */
  moving: SnapRect;
  /** Pointer position in canvas space, used to break candidate ties. */
  cursor: SnapPoint;
  /** True while the user holds Ctrl, which disables guides and snapping. */
  disabled: boolean;
  /**
   * Optional snap radius in screen pixels (converted to canvas via zoom).
   * Defaults to the pointer-drag threshold when omitted.
   */
  thresholdScreenPx?: number;
}

export interface CanvasDragSnapOffset {
  dx: number;
  dy: number;
}

export interface CanvasSnapContextValue {
  /** True when several nodes are selected and moved as a single group. */
  isMultiSelection: boolean;
  /** Converts viewport coordinates into canvas (world) coordinates. */
  clientToCanvasPoint: (clientX: number, clientY: number) => SnapPoint;
  /**
   * Canvas-space origin of the coordinate space holding a node `x` / `y`.
   * Children of absolute containers are flattened onto the canvas and thus use
   * `(0, 0)`, whereas DOM-nested children are relative to their parent box.
   */
  getNodeCanvasOrigin: (nodeId: string) => SnapPoint;
  /** Resolves the snapped offset and publishes the matching guides. */
  resolveDragSnap: (request: CanvasDragSnapRequest) => CanvasDragSnapOffset;
  /** Hides both guides, typically at the end of a gesture. */
  clearGuides: () => void;
  /** Starts moving the whole selection from a pointer event on a node. */
  beginGroupDrag: (event: React.PointerEvent<HTMLElement>) => void;
}

const CanvasSnapContext = createContext<CanvasSnapContextValue | null>(null);

export const CanvasSnapProvider = CanvasSnapContext.Provider;

/** Returns the snap contract, or `null` when rendered outside the editor canvas. */
export const useCanvasSnap = (): CanvasSnapContextValue | null =>
  useContext(CanvasSnapContext);
